// Клиент-обёртка над geometry.worker.js.
// Используется в UI-потоке.
//
// Использование:
//   import { GeometryClient } from './geometryClient';
//   const client = new GeometryClient();
//   await client.init();                          // загружает WASM
//   const mesh = await client.createTestCube();   // строит куб, возвращает {bodyId, positions, indices, ...}
//   const stlBlob = await client.exportSTL(bodyId);
//
// Внутри — простой request/response поверх postMessage с автогенерацией id.

import useCadStore from './store/useCadStore';
import { reportCrash } from './crashReporter';


export class GeometryClient {
    constructor() {
        this.worker = null;
        this.ready = false;
        // Очередь mesh-операций — последовательная цепочка Promise.
        // Каждый новый запрос на mesh цепляется в .then() предыдущего —
        // OCCT/replicad плохо переваривают конкурирующие операции на shape.
        this._meshChain = Promise.resolve();
        // requestId-протокол: каждый запрос к воркеру получает уникальный id;
        // ответ (OP_DONE / *_DATA / WORKER_ERROR) адресуется по этому id.
        // Это исключает путаницу «поздний ответ от прошлой операции резолвит
        // слот текущей» и позволяет паттернам слать N мешей + один OP_DONE.
        this._pending = new Map();   // requestId → { resolve, reject, timer }
        this._reqSeq = 0;
    }

    _nextRequestId() {
        this._reqSeq = (this._reqSeq + 1) % 0x7fffffff;
        return 'req_' + this._reqSeq;
    }

    /** Завершить ожидающий запрос (успех или ошибка) по requestId. */
    _settle(requestId, err, value) {
        if (requestId == null) return;
        const slot = this._pending.get(requestId);
        if (!slot) return;
        this._pending.delete(requestId);
        if (slot.timer) clearTimeout(slot.timer);
        if (err) slot.reject(err);
        else slot.resolve(value);
    }

    /**
     * Сериализатор mesh-операций. Принимает функцию, которая стартует
     * worker-команду через postMessage и возвращает Promise. Гарантирует
     * что одновременно выполняется не более одной операции.
     */
    _enqueueMesh(taskFn) {
        const next = this._meshChain.then(
            () => taskFn(),
            () => taskFn(),  // даже если предыдущая упала — следующая выполняется
        );
        // Цепочка не должна «помнить» ошибки предыдущих задач для следующих.
        this._meshChain = next.catch(() => {});
        return next;
    }

    init() {
        return new Promise((resolve, reject) => {
            try {
                // Module Worker (type: 'module'). Внутри Worker'а используем
                // динамический import() для замены Emscripten-модуля и
                // обычные ES-import'ы для replicad. Поддерживается Chrome/Edge
                // (с 2018), Firefox 114+ (с июня 2023). Для нашего CAD
                // (только десктоп) это нормально.
                this.worker = new Worker(
                    new URL('./workers/geometry.worker.js', import.meta.url),
                    { type: 'module' }
                );
            } catch (e) {
                reject(e);
                return;
            }
            // Слушаем все сообщения и роутим по типу
            this.worker.onmessage = (e) => this._dispatch(e.data);
            this.worker.onerror = (e) => {
                console.error('[geometry.worker] error', e);
                useCadStore.getState().setKernelStatus('error', String(e.message || e));
                if (!this.ready) reject(e);
            };
            // Сохраняем resolve чтобы вызвать когда придёт KERNEL_READY
            this._initResolve = resolve;
            this._initReject = reject;
            // Старт инициализации
            useCadStore.getState().setKernelStatus('loading');
            this.worker.postMessage({ type: 'INIT' });
        });
    }

    _dispatch(msg) {
        switch (msg.type) {
            case 'KERNEL_READY':
                this.ready = true;
                useCadStore.getState().setKernelStatus('ready');
                if (this._initResolve) this._initResolve();
                break;

            case 'KERNEL_ERROR':
                this.ready = false;
                useCadStore.getState().setKernelStatus('error', msg.message);
                reportCrash({
                    level: 'error',
                    category: 'replicad',
                    message: 'Ядро WASM не загрузилось: ' + (msg.message || ''),
                    stack: msg.stack,
                });
                if (this._initReject) this._initReject(new Error(msg.message));
                break;

            case 'BODY_MESH':
                useCadStore.getState().setBodyMesh(msg.bodyId, {
                    positions:   msg.positions,
                    indices:     msg.indices,
                    normals:     msg.normals,
                    edges:       msg.edges,
                    edgeIds:     msg.edgeIds,
                    edgeOffsets: msg.edgeOffsets,
                    // Устойчивые дескрипторы рёбер (midpoint+length из B-rep) —
                    // для надёжного сопоставления при fillet/chamfer и rebuild.
                    edgeMids:    msg.edgeMids,
                    edgeLens:    msg.edgeLens,
                });
                // BODY_MESH — промежуточное сообщение (обновляет store).
                // Promise операции резолвится ПОЗЖЕ, по OP_DONE. Это ключевое
                // отличие: паттерн шлёт N мешей, и только потом OP_DONE.
                break;

            case 'OP_DONE': {
                // Успешное завершение mesh-операции — резолвим её Promise.
                // bodyId = последнее затронутое тело (результат combine/duplicate/
                // primitive); bodyIds — полный список (для паттернов).
                const ids = msg.bodyIds || [];
                this._settle(msg.requestId, null, {
                    command: msg.command,
                    bodyIds: ids,
                    bodyId: ids.length ? ids[ids.length - 1] : undefined,
                });
                break;
            }

            case 'STL_DATA':
            case 'STEP_DATA':
            case 'OBJ_DATA':
                this._settle(msg.requestId, null, msg.data);
                break;

            case 'STL_ERROR':
            case 'STEP_ERROR':
            case 'OBJ_ERROR':
                this._settle(msg.requestId, new Error(msg.message || 'Ошибка экспорта'));
                break;

            case 'MEASURE_DATA':
                this._settle(msg.requestId, null, {
                    volume: msg.volume, area: msg.area, com: msg.com, bbox: msg.bbox,
                    // holeCount — оценка числа отверстий для автопроверки уроков.
                    holeCount: msg.holeCount,
                });
                break;
            case 'MEASURE_ERROR':
                this._settle(msg.requestId, new Error(msg.message || 'Ошибка измерения'));
                break;

            // Контуры букв для вставки в эскиз (текст как эскиз, а не тело).
            case 'TEXT_CONTOURS':
                if (msg.error) this._settle(msg.requestId, new Error(msg.error));
                else this._settle(msg.requestId, null, msg.contours);
                break;

            case 'MEASURE_RESULT':
                if (msg.error) {
                    this._settle(msg.requestId, new Error(msg.error));
                } else {
                    // Отдаём весь результат измерения (distance/angle/delta/
                    // entities/p1/p2) как объект — фронт разберёт сам.
                    const { type: _t, requestId: _r, ...data } = msg;
                    this._settle(msg.requestId, null, data);
                }
                break;

            case 'WORKER_ERROR':
                console.error('[geometry.worker] WORKER_ERROR', msg);
                // Шлём в телеметрию
                reportCrash({
                    level: 'error',
                    category: 'worker',
                    message: `[${msg.command || 'unknown'}] ${msg.message || 'Ошибка операции'}`,
                    stack: msg.stack,
                });
                // НЕ ставим kernelStatus=error — ядро живо, ошибка в команде.
                // Отклоняем именно тот запрос, который упал (по requestId).
                this._settle(msg.requestId, new Error(msg.message || 'Ошибка операции'));
                break;

            case 'BODIES_CLEARED':
                // Очищаем все mesh'и в store. Promise clearAll резолвится
                // отдельно по OP_DONE (requestId-протокол).
                useCadStore.getState().clearBodyMeshes();
                break;

            case 'BODY_REMOVED': {
                // Удаляем mesh из store при удалении тела (после boolean-ops)
                const store = useCadStore.getState();
                const m = new Map(store.bodyMeshes);
                m.delete(msg.bodyId);
                useCadStore.setState({ bodyMeshes: m });
                break;
            }

            default:
                console.warn('[geometryClient] unknown msg type:', msg.type, msg);
        }
    }

    /** Универсальный запрос к воркеру по requestId-протоколу.
     *  Сериализуется через _enqueueMesh (воркер однопоточный).
     *  Резолвится по OP_DONE / *_DATA с тем же requestId, отклоняется по
     *  WORKER_ERROR / *_ERROR / таймауту. */
    _request(type, payload, opName, timeoutMs = 30000, transferable = []) {
        return this._enqueueMesh(() => new Promise((resolve, reject) => {
            const requestId = this._nextRequestId();
            const timer = setTimeout(() => {
                // Таймаут: снимаем слот и отклоняем. Операция в воркере может
                // ещё идти, но её поздний ответ теперь никого не заденет —
                // _settle по этому requestId уже ничего не найдёт.
                this._settle(requestId, new Error(`Таймаут: ${opName} занял слишком много времени`));
            }, timeoutMs);
            this._pending.set(requestId, { resolve, reject, timer });
            try {
                this.worker.postMessage({ type, payload, requestId }, transferable);
            } catch (e) {
                this._settle(requestId, e);
            }
        }));
    }

    /** @deprecated Сохранено как алиас — все вызовы идут через _request. */
    _meshOperation(type, payload, opName, timeoutMs = 30000, transferable = []) {
        return this._request(type, payload, opName, timeoutMs, transferable);
    }

    createTestCube() {
        return this._meshOperation('CREATE_TEST_CUBE', undefined, 'createTestCube');
    }

    /** Создать примитив (box/cylinder/sphere/torus) — payload параметров. */
    createPrimitive(payload) {
        return this._meshOperation('CREATE_PRIMITIVE', payload, 'createPrimitive');
    }

    /** Выдавить профиль эскиза в 3D-тело. */
    extrudeSketchProfile(payload) {
        return this._meshOperation('EXTRUDE_SKETCH_PROFILE', payload, 'extrude');
    }

    /** Масштаб тела относительно центра его габаритов. */
    scaleBody(bodyId, factor) {
        return this._meshOperation('SCALE_BODY', { bodyId, factor }, 'масштаб');
    }

    /** Спираль (пружина/резьба): сечение по винтовой траектории. */
    createCoil(payload) {
        return this._meshOperation('CREATE_COIL', payload, 'спираль');
    }

    /** Текст → объёмные буквы на плоскости (надпись, гравировка). */
    createText(payload) {
        return this._meshOperation('CREATE_TEXT', payload, 'text');
    }

    /** Вращение профиля эскиза вокруг оси → 3D-тело. */
    revolveSketchProfile(payload) {
        return this._meshOperation('REVOLVE_SKETCH_PROFILE', payload, 'revolve');
    }

    /** Логические операции между двумя готовыми телами.
     *  payload = { targetId, toolId, operation: 'add'|'cut'|'intersect', keepTool? } */
    combineBodies(payload) {
        return this._meshOperation('COMBINE_BODIES', payload, 'combine');
    }

    /** Sweep — протянуть профиль вдоль траектории. */
    sweepProfile(payload) {
        return this._request('SWEEP_PROFILE', payload, 'sweep', 40000);
    }

    /** Loft — переход между профилями на разных плоскостях. */
    loftProfiles(payload) {
        return this._request('LOFT_PROFILES', payload, 'loft', 40000);
    }

    /** Fillet/Chamfer.
     *  payload = { bodyId, edgeIds, edgeDescs?, edgeEndpoints?, radius, kind, preview? }
     *  edgeDescs = [{ mid:[x,y,z], len }] — устойчивые дескрипторы рёбер
     *  (основной механизм сопоставления, переживает rebuild). */
    filletOrChamfer(payload) {
        return this._meshOperation('FILLET_OR_CHAMFER', payload, 'fillet/chamfer');
    }

    /** Отверстие — цилиндрический вырез через грань.
     *  payload = { targetBodyId, planeRef, faceCenter, offsetX, offsetY,
     *              diameter, depth, depthType } */
    createHole(payload) {
        return this._meshOperation('CREATE_HOLE', payload, 'отверстие');
    }

    /** Зеркало — зеркальная копия тела относительно плоскости.
     *  payload = { sourceBodyId, plane: 'XY'|'XZ'|'YZ' } */
    mirrorBody(payload) {
        return this._meshOperation('MIRROR_BODY', payload, 'зеркало');
    }

    /** Прямоугольный массив — сетка N×M копий тела.
     *  payload = { sourceBodyId, countX, countY, stepX, stepY } */
    rectPattern(payload) {
        return this._meshOperation('RECT_PATTERN', payload, 'массив');
    }

    /** Круговой массив — N копий тела по окружности вокруг оси.
     *  payload = { sourceBodyId, axis: 'X'|'Y'|'Z', count, angle } */
    circPattern(payload) {
        return this._meshOperation('CIRC_PATTERN', payload, 'круговой массив');
    }

    /** Откат preview fillet/chamfer — восстановить оригинал тела из backup. */
    cancelFilletPreview(bodyId) {
        try {
            this.worker.postMessage({
                type: 'CANCEL_FILLET_PREVIEW',
                payload: { bodyId },
            });
        } catch (e) {
            console.error('[geometryClient] cancelFilletPreview failed:', e);
        }
    }

    /** Переместить или скопировать тело.
     *  payload = { bodyId, dx, dy, dz, rx, ry, rz, copy, preview } */
    /**
     * Поставить тело в положение, заданное соединением.
     * payload: { bodyId, kind, value, origin:[x,y,z], axis:[x,y,z] }
     */
    applyJoint(payload) {
        return this._meshOperation('APPLY_JOINT', payload, 'joint');
    }

    /**
     * Ребро жёсткости (rib) или стенка (web) из незамкнутой линии эскиза.
     * payload: { points, planeName, planeOffset, customPlane,
     *            thickness, depth, kind, targetBodyId }
     */
    createRib(payload) {
        return this._meshOperation('CREATE_RIB', payload, 'rib/web');
    }

    moveOrCopyBody(payload) {
        return this._meshOperation('MOVE_OR_COPY_BODY', payload, 'move/copy');
    }

    /** Откат preview move/copy — восстановить оригинал и удалить preview-копию. */
    cancelMoveCopyPreview(bodyId) {
        try {
            this.worker.postMessage({
                type: 'CANCEL_MOVE_COPY_PREVIEW',
                payload: { bodyId },
            });
        } catch (e) {
            console.error('[geometryClient] cancelMoveCopyPreview failed:', e);
        }
    }

    /** Дублировать тело — создать копию shape под новым bodyId.
     *  Резолвится с { bodyId: newId } когда mesh нового тела пришёл в UI. */
    duplicateBody(sourceBodyId) {
        return this._meshOperation('DUPLICATE_BODY', { sourceBodyId }, 'duplicate');
    }

    /** Оболочка (shell). payload = { bodyId, thickness, faceCenters, preview? } */
    shellBody(payload) {
        return this._meshOperation('SHELL_BODY', payload, 'shell');
    }

    /** Откат preview shell. */
    cancelShellPreview(bodyId) {
        try {
            this.worker.postMessage({
                type: 'CANCEL_SHELL_PREVIEW',
                payload: { bodyId },
            });
        } catch (e) {
            console.error('[geometryClient] cancelShellPreview failed:', e);
        }
    }

    exportSTL(bodyId) {
        return this._request('EXPORT_STL', { bodyId }, 'экспорт STL', 60000);
    }

    /** Экспорт STEP — точная B-rep геометрия для CAD-обмена. */
    exportSTEP(bodyId) {
        return this._request('EXPORT_STEP', { bodyId }, 'экспорт STEP', 60000);
    }

    /** Экспорт OBJ — текстовый mesh для Blender / 3D-Max / Unity. */
    exportOBJ(bodyId) {
        return this._request('EXPORT_OBJ', { bodyId }, 'экспорт OBJ', 60000);
    }

    /** Измерить тело: { volume, area, com, bbox }. */
    measureBody(bodyId) {
        return this._request('MEASURE_BODY', { bodyId }, 'измерение', 15000);
    }

    /** Измерить сущности (грань/ребро/вершина). picks: 1 или 2 элемента вида
     *  { bodyId, kind:'face'|'edge'|'vertex', point:[x,y,z], normal?, mid?, len? }.
     *  Возвращает { count, entities, distance?, angle?, delta?, p1?, p2?, bothVertices? }. */
    measure(picks) {
        return this._request('MEASURE', { picks }, 'измерение', 15000);
    }

    /** Контуры букв для вставки в эскиз. Первый вызов грузит шрифт (~1 МБ),
     *  поэтому таймаут больше обычного. */
    textToContours(text, fontSize) {
        return this._request('TEXT_TO_CONTOURS', { text, fontSize }, 'текст', 20000);
    }

    /** Импорт STEP — загрузить чужой .step файл как новое тело.
     *  Может занять 5-30 секунд на сложных файлах — таймаут 60с.
     *  Transferable: ArrayBuffer передаётся по zero-copy в worker. */
    importSTEP(fileBuffer, filename = '') {
        return this._meshOperation(
            'IMPORT_STEP',
            { fileBuffer, filename },
            'импорт STEP',
            60000,
            [fileBuffer],
        );
    }

    clearAll() {
        // Резолвится по OP_DONE (через _request); BODIES_CLEARED параллельно
        // чистит store. Очередь _enqueueMesh гарантирует, что clearAll не
        // пересечётся с идущей mesh-операцией.
        return this._request('CLEAR_ALL', undefined, 'очистка сцены', 15000);
    }

    /** Удалить одно тело в worker'е (вызывает .delete() на shape, освобождая
     *  WASM-память). После этого worker отправит BODY_REMOVED → store
     *  очистит mesh из bodyMeshes. */
    deleteBody(bodyId) {
        if (!this.worker || !bodyId) return;
        this.worker.postMessage({ type: 'DELETE_BODY', payload: { bodyId } });
    }

    destroy() {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
            this.ready = false;
        }
    }
}


// Синглтон-инстанс на всё приложение
let _instance = null;

export function getGeometryClient() {
    if (!_instance) _instance = new GeometryClient();
    return _instance;
}
